import React, { useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import './Home.scss';

// main.html 시안 기반

const CONTACT_EMAIL = 'contact@yeplat.com';

const RIBBON_ITEMS = [
  '기획',
  '디자인',
  '앱 개발',
  '웹 플랫폼',
  'SaaS',
  '관리자 시스템',
  '운영 고도화',
  'API 설계',
  '클라우드',
];

const STATEMENTS = [
  {
    title: '사용자 경험과 기술을 연결',
    body: '기획 단계의 문제 정의부터 화면 설계, 구현, 출시 이후의 개선까지 하나의 흐름으로 이어갑니다.',
  },
  {
    title: '빠른 실행력',
    body: '아이디어를 빠르게 제품으로 옮기고, 짧은 사이클로 검증하며 완성도를 끌어올립니다.',
  },
  {
    title: '운영까지 고려한 설계',
    body: '확장 가능한 아키텍처와 운영 구조를 함께 설계해, 서비스가 오래 안정적으로 자라도록 돕습니다.',
  },
];

interface ServiceItem {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  tags: string[];
}

const SERVICES: ServiceItem[] = [
  {
    id: 'app-development',
    title: '앱 개발',
    subtitle: 'App Development',
    description:
      'iOS·Android 앱 개발부터 UX 최적화, 유지보수까지 제품 완성도와 운영 안정성을 함께 설계합니다.',
    tags: ['iOS', 'Android', 'Flutter', 'React Native'],
  },
  {
    id: 'web-platform',
    title: '웹 플랫폼 개발',
    subtitle: 'Web Platform',
    description:
      'SaaS, B2B, B2C에 적합한 웹 플랫폼을 설계하고, 확장 가능한 아키텍처와 운영 구조를 제공합니다.',
    tags: ['Web', 'SaaS', 'CMS', 'Cloud'],
  },
];

const CAPABILITIES = [
  { title: '플랫폼 설계', body: '비즈니스 모델에 맞는 서비스 구조와 데이터 흐름을 설계합니다.' },
  { title: '관리자 시스템', body: '운영자가 직접 쓰는 백오피스와 대시보드를 구축합니다.' },
  { title: '운영 고도화', body: '런칭 이후 기능 개선과 성능·안정성 관리까지 이어갑니다.' },
  { title: '유지보수', body: '지속 가능한 코드와 문서로 오래 관리되는 제품을 만듭니다.' },
];

const STEPS = [
  {
    title: '기획 · 전략',
    body: '비즈니스 목표와 사용자 맥락을 정리하고, 만들 제품의 방향과 범위를 정의합니다.',
    phase: 'DISCOVERY / SCOPE / ROADMAP',
  },
  {
    title: '디자인 · 프로토타입',
    body: 'UX·UI 설계와 인터랙션 프로토타입으로, 개발 전에 완성도와 흐름을 미리 검증합니다.',
    phase: 'UX / UI / PROTOTYPE',
  },
  {
    title: '개발 · 구현',
    body: '앱과 웹 플랫폼을 구현하고, 확장을 고려한 아키텍처로 설계해 안정성을 확보합니다.',
    phase: 'APP / WEB / API / CLOUD',
  },
  {
    title: '운영 · 고도화',
    body: '런칭 이후 운영과 지속적인 개선을 함께하며, 서비스를 오래 안정적으로 키워갑니다.',
    phase: 'LAUNCH / OPERATION / ITERATION',
  },
];

const STACKS = [
  { title: 'Mobile', chips: ['iOS', 'Android', 'Flutter', 'React Native'] },
  { title: 'Web & Platform', chips: ['Web', 'SaaS', 'CMS', 'Admin', 'API'] },
  { title: 'Infra & Cloud', chips: ['Cloud', 'CI/CD', 'Monitoring'] },
];

const CHART_BARS = [34, 52, 44, 68, 58, 82, 72, 96];

const pad2 = (n: number) => String(n).padStart(2, '0');

const Home: React.FC = () => {
  const rootRef = useRef<HTMLDivElement>(null);

  // 진입 애니메이션 (JS가 동작하지 않아도 콘텐츠는 항상 보임)
  // 첫 페인트 전에 숨김 상태를 잡아야 깜박이지 않으므로 useLayoutEffect 사용
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!('IntersectionObserver' in window) || reduce) return;

    // 함께 나타나는 요소들은 순서대로 조금씩 늦게 등장
    const show = (els: Element[]) => {
      els.forEach((el, i) => {
        (el as HTMLElement).style.setProperty('--rd', `${i * 0.2}s`);
        el.classList.add('in');
      });
    };

    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).map((entry) => entry.target);
        visible.forEach((el) => io.unobserve(el));
        show(visible);
      },
      { threshold: 0.14, rootMargin: '0px 0px -8% 0px' }
    );

    root.classList.add('reveal-ready');
    const initial: Element[] = [];
    root.querySelectorAll<HTMLElement>('.reveal').forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight * 0.92 && r.bottom > 0) initial.push(el);
      else io.observe(el);
    });
    show(initial);

    return () => {
      io.disconnect();
      root.classList.remove('reveal-ready');
    };
  }, []);

  // 카드 스포트라이트
  const handleCardMove = (ev: React.PointerEvent<HTMLElement>) => {
    const card = ev.currentTarget;
    const r = card.getBoundingClientRect();
    card.style.setProperty('--mx', `${ev.clientX - r.left}px`);
    card.style.setProperty('--my', `${ev.clientY - r.top}px`);
  };

  return (
    <div className="home" ref={rootRef}>
      <div className="home-field" aria-hidden="true">
        <div className="glow" />
        <div className="glow b" />
        <div className="grid" />
        <div className="scan" />
      </div>

      <Header />

      <main id="top">
        {/* HERO */}
        <section className="hero">
          <div className="wrap hero-grid">
            <div>
              <span className="eyebrow">Digital Product Studio</span>
              <h1 className="hero-title">
                <span className="ln">
                  <span>아이디어를 제품으로,</span>
                </span>
                <span className="ln">
                  <span>
                    비즈니스를 <span className="em">플랫폼</span>으로.
                  </span>
                </span>
              </h1>
              <p className="hero-sub reveal">
                예플랫은 앱 개발과 웹 플랫폼 구축을 중심으로, 기획부터 디자인·개발·운영까지
                연결되는 디지털 서비스를 만듭니다. 빠른 실행력과 안정적인 기술 설계로 비즈니스의
                성장을 돕습니다.
              </p>
              <div className="hero-actions reveal">
                <Link className="btn btn-primary" to="/inquiry">
                  상담 요청하기 <span className="ar">↗</span>
                </Link>
                <a className="btn btn-ghost" href="#services">
                  서비스 살펴보기 <span className="ar">↓</span>
                </a>
              </div>
              <div className="hero-meta reveal">
                <div>
                  ONE-STOP<b>기획부터 운영까지</b>
                </div>
                <div>
                  PRODUCT<b>앱 · 웹 플랫폼</b>
                </div>
                <div>
                  PARTNERSHIP<b>하나의 팀처럼</b>
                </div>
              </div>
            </div>

            <div className="spec reveal">
              <div className="spec-card">
                <div className="spec-head">
                  <i className="dot" />
                  <i className="dot" />
                  <i className="dot i" />
                  <span>yeplat / product-build</span>
                </div>
                <div className="spec-body">
                  <div className="spec-kpis">
                    {['PLAN', 'BUILD', 'SCALE'].map((label, i) => (
                      <div key={label} className="kpi">
                        <b>{pad2(i + 1)}</b>
                        <small>{label}</small>
                      </div>
                    ))}
                  </div>
                  <div className="srow">
                    <span className="k">UX</span>
                    <span className="bar" />
                  </div>
                  <div className="srow">
                    <span className="k">APP</span>
                    <span className="bar m" />
                  </div>
                  <div className="srow">
                    <span className="k">WEB</span>
                    <span className="bar s" />
                  </div>
                  <div className="chart">
                    {CHART_BARS.map((h, i) => (
                      <i
                        key={i}
                        style={{ height: `${h}%`, animationDelay: `${0.4 + i * 0.15}s` }}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <span className="spec-tag top">// 아이디어 → 제품</span>
              <span className="spec-tag">// 안정적인 운영 설계</span>
            </div>
          </div>
          <div className="ruler" aria-hidden="true">
            {Array.from({ length: 30 }, (_, i) => (
              <i key={i} />
            ))}
          </div>
        </section>

        {/* RIBBON */}
        <div className="ribbon" aria-hidden="true">
          <div className="ribbon-track">
            {[...RIBBON_ITEMS, ...RIBBON_ITEMS].map((item, i) => (
              <span key={i}>{item}</span>
            ))}
          </div>
        </div>

        {/* STATEMENT */}
        <section className="statement" id="about">
          <div className="wrap">
            <span className="eyebrow reveal">Our Studio</span>
            <p className="lead reveal">
              사용자 경험과 기술을,
              <br />
              하나의 <em>흐름</em>으로 연결합니다.
            </p>
            <div className="body">
              {STATEMENTS.map((item, i) => (
                <div key={item.title} className="reveal">
                  <span className="n">/ {pad2(i + 1)}</span>
                  <b>{item.title}</b>
                  <p>{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section className="services" id="services">
          <div className="wrap">
            <div className="sec-head">
              <div>
                <span className="eyebrow reveal">Our Services</span>
                <h2 className="sec-title reveal">
                  아이디어부터 운영까지,
                  <br />
                  예플랫이 함께합니다.
                </h2>
              </div>
              <p className="sec-note reveal">
                사용자 경험과 기술을 연결하여 지속 가능한 디지털 서비스를 구축합니다. 앱 개발,
                플랫폼 설계, 관리자 시스템, 운영 고도화까지 하나의 팀처럼 밀도 있게 지원합니다.
              </p>
            </div>

            <div className="cards">
              {SERVICES.map((service, i) => (
                <Link
                  key={service.id}
                  to="/about"
                  className="card reveal"
                  onPointerMove={handleCardMove}
                >
                  <div className="card-top">
                    <span className="idx">SERVICE / {pad2(i + 1)}</span>
                    <span className="go">↗</span>
                  </div>
                  <h3>
                    {service.title}
                    <br />
                    {service.subtitle}
                  </h3>
                  <p>{service.description}</p>
                  <div className="tags">
                    {service.tags.map((tag) => (
                      <span key={tag}>{tag}</span>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="caps-band">
            <div className="wrap caps reveal">
              {CAPABILITIES.map((cap) => (
                <div key={cap.title} className="c">
                  <b>{cap.title}</b>
                  <p>{cap.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PROCESS */}
        <section className="process" id="process">
          <div className="wrap">
            <div className="sec-head">
              <div>
                <span className="eyebrow reveal">Process</span>
                <h2 className="sec-title reveal">
                  하나의 팀처럼,
                  <br />
                  네 단계로 완성합니다.
                </h2>
              </div>
              <p className="sec-note reveal">
                기획부터 운영까지 끊김 없이 이어지는 프로세스로, 아이디어를 안정적인 제품으로
                만듭니다.
              </p>
            </div>

            <div className="steps">
              {STEPS.map((step, i) => (
                <div key={step.title} className="step reveal">
                  <div className="no">STEP {pad2(i + 1)}</div>
                  <div>
                    <h4>{step.title}</h4>
                    <p>{step.body}</p>
                    <span className="ph">{step.phase}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* STACK */}
        <section className="stack" id="stack">
          <div className="wrap">
            <span className="eyebrow reveal">Technology</span>
            <h2 className="sec-title reveal">제품을 지탱하는 기술.</h2>
            <div className="stack-grid">
              {STACKS.map((stack) => (
                <div key={stack.title} className="st reveal">
                  <h5>{stack.title}</h5>
                  <div className="chips">
                    {stack.chips.map((chip) => (
                      <span key={chip}>{chip}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="cta" id="contact">
          <div className="grid-bg" aria-hidden="true" />
          <div className="wrap">
            <div className="cta-inner reveal">
              <span className="eyebrow">Start a Project</span>
              <h2>
                브랜드에 맞는 제품과 플랫폼,
                <br />
                예플랫이 함께 설계합니다.
              </h2>
              <p>
                신규 서비스 런칭, 레거시 고도화, 운영 효율 개선까지. 비즈니스 목표에 맞는 디지털
                제품을 빠르고 안정적으로 구현하세요.
              </p>
              <div className="hero-actions">
                <Link className="btn btn-primary" to="/inquiry">
                  상담 요청하기 <span className="ar">↗</span>
                </Link>
                <a className="btn btn-ghost" href="#services">
                  서비스 다시 보기
                </a>
              </div>
              <a className="cta-mail" href={`mailto:${CONTACT_EMAIL}`}>
                {CONTACT_EMAIL}
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Home;
